/**
 * GCS storage resolver for the Node host pack (and server adapter hosts).
 * Service-account JWT → OAuth access token → `storage/v1` media download.
 * Runtime-agnostic (crypto.subtle + fetch), so the same resolver works in
 * Workers and Node. Bucket/key never enter error payloads; private causes
 * restore only at Promise compatibility edges.
 */

import { Effect, Layer, Option } from 'effect';

import {
  DocumentSource,
  ParserHttp,
  ParserSecrets,
  makeParserDependencyError,
  type ParseDocumentRef,
  type ParserDependencyError,
} from '../parser-host';

interface GcsServiceAccountJson {
  readonly client_email: string;
  readonly private_key: string;
  readonly project_id?: string;
}

function base64UrlEncode(value: string): string {
  return btoa(value).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

type SubtleCryptoKey = Parameters<typeof crypto.subtle.sign>[1];

async function importPrivateKey(pem: string): Promise<SubtleCryptoKey> {
  const b64 = pem
    .replace(/-----BEGIN PRIVATE KEY-----/, '')
    .replace(/-----END PRIVATE KEY-----/, '')
    .replace(/\s/g, '');
  const binary = Uint8Array.from(atob(b64), (character) => character.charCodeAt(0));
  return crypto.subtle.importKey(
    'pkcs8',
    binary,
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    false,
    ['sign'],
  );
}

async function createJwt(account: GcsServiceAccountJson): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const header = { alg: 'RS256', typ: 'JWT' };
  const payload = {
    iss: account.client_email,
    scope: 'https://www.googleapis.com/auth/devstorage.read_only',
    aud: 'https://oauth2.googleapis.com/token',
    iat: now,
    exp: now + 3600,
  };
  const encode = (value: unknown) => base64UrlEncode(JSON.stringify(value));
  const unsigned = `${encode(header)}.${encode(payload)}`;
  const key = await importPrivateKey(account.private_key);
  const signature = await crypto.subtle.sign(
    'RSASSA-PKCS1-v1_5',
    key,
    new TextEncoder().encode(unsigned),
  );
  const signatureB64 = btoa(String.fromCharCode(...new Uint8Array(signature)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
  return `${unsigned}.${signatureB64}`;
}

function storageFailure(
  operation: string,
  code: string,
  retryable: boolean,
  cause?: unknown,
): ParserDependencyError {
  return makeParserDependencyError(
    { service: 'document-source', operation, code, retryable },
    cause,
  );
}

/** Parse the GCS service-account JSON secret (raw JSON, validated lazily). */
export function parseGcsServiceAccount(raw: string): GcsServiceAccountJson {
  return JSON.parse(raw) as GcsServiceAccountJson;
}

const tokenCache = new Map<string, { token: string; expiresAt: number }>();

/**
 * Service-free GCS object download (JWT → token → media bytes). The token
 * cache is keyed by service-account email so hosts and services share it.
 */
export const gcsObjectBytesEffect = Effect.fn('okrapdf/parser-sdk.gcs.objectBytes')(function* (
  fetchEffect: (
    input: string | URL,
    init?: RequestInit,
  ) => Effect.Effect<Response, ParserDependencyError>,
  account: GcsServiceAccountJson,
  bucket: string,
  key: string,
): Effect.fn.Return<Uint8Array, ParserDependencyError> {
  const cached = tokenCache.get(account.client_email);
  let token = cached && Date.now() < cached.expiresAt ? cached.token : undefined;
  if (token === undefined) {
    const jwt = yield* Effect.tryPromise({
      try: () => createJwt(account),
      catch: (cause) => storageFailure('gcs-auth', 'credentials_invalid', false, cause),
    });
    const tokenResponse = yield* fetchEffect('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `grant_type=urn:ietf:params:oauth:grant-type:jwt-bearer&assertion=${jwt}`,
    }).pipe(
      Effect.mapError((cause) => storageFailure('gcs-auth', 'token_request_failed', true, cause)),
    );
    if (!tokenResponse.ok) {
      const detail = yield* Effect.tryPromise({
        try: () => tokenResponse.text(),
        catch: () => undefined,
      }).pipe(Effect.catch(() => Effect.succeed('')));
      return yield* storageFailure(
        'gcs-auth',
        'token_rejected',
        false,
        new Error(`GCS OAuth failed: ${tokenResponse.status} ${detail.slice(0, 200)}`),
      );
    }
    const data = yield* Effect.tryPromise({
      try: () => tokenResponse.json() as Promise<{ access_token: string; expires_in: number }>,
      catch: (cause) => storageFailure('gcs-auth', 'token_decode_failed', false, cause),
    });
    tokenCache.set(account.client_email, {
      token: data.access_token,
      expiresAt: Date.now() + (data.expires_in - 300) * 1000,
    });
    token = data.access_token;
  }
  const url = `https://storage.googleapis.com/storage/v1/b/${encodeURIComponent(bucket)}/o/${encodeURIComponent(key)}?alt=media`;
  const response = yield* fetchEffect(url, {
    headers: { Authorization: `Bearer ${token}` },
  }).pipe(
    Effect.mapError((cause) => storageFailure('gcs-get', 'object_request_failed', true, cause)),
  );
  if (!response.ok) {
    const detail = yield* Effect.tryPromise({
      try: () => response.text(),
      catch: () => undefined,
    }).pipe(Effect.catch(() => Effect.succeed('')));
    return yield* storageFailure(
      'gcs-get',
      'object_rejected',
      response.status >= 500,
      new Error(`GCS fetch ${response.status}: ${detail.slice(0, 200)}`),
    );
  }
  return yield* Effect.tryPromise({
    try: async () => new Uint8Array(await response.arrayBuffer()),
    catch: (cause) => storageFailure('gcs-get', 'object_read_failed', false, cause),
  });
});

const requireAccount = Effect.fn('okrapdf/parser-sdk.gcs.requireAccount')(function* () {
  const secrets = yield* ParserSecrets;
  const raw = Option.getOrUndefined(yield* secrets.get('GCS_SERVICE_ACCOUNT_KEY'));
  if (!raw) {
    return yield* storageFailure(
      'gcs-auth',
      'credentials_unavailable',
      false,
      new Error('GCS_SERVICE_ACCOUNT_KEY not configured'),
    );
  }
  return yield* Effect.try({
    try: () => parseGcsServiceAccount(raw),
    catch: (cause) => storageFailure('gcs-auth', 'credentials_invalid', false, cause),
  });
});

/** A GCS-backed DocumentSource service implementation (bytes refs pass through). */
export function gcsDocumentSourceService(): {
  readonly bytes: (
    document: ParseDocumentRef,
  ) => Effect.Effect<Uint8Array, ParserDependencyError, ParserHttp | ParserSecrets>;
} {
  return {
    bytes: (document) => {
      if (document.kind !== 'storage') return Effect.succeed(document.bytes);
      if (document.provider !== 'gcs') {
        return Effect.fail(
          storageFailure(
            'gcs-get',
            'provider_unsupported',
            false,
            new Error(`GCS DocumentSource cannot resolve provider '${document.provider}'`),
          ),
        );
      }
      return Effect.gen(function* () {
        const http = yield* ParserHttp;
        const account = yield* requireAccount();
        return yield* gcsObjectBytesEffect(
          (input, init) => http.fetch(input, init),
          account,
          document.bucket,
          document.key,
        );
      });
    },
  };
}

/** The GCS DocumentSource as a Layer (requires ParserHttp + ParserSecrets). */
export function gcsDocumentSourceLayer(): Layer.Layer<
  DocumentSource,
  never,
  ParserHttp | ParserSecrets
> {
  const service = gcsDocumentSourceService();
  return Layer.effect(
    DocumentSource,
    Effect.gen(function* () {
      const context = yield* Effect.context<ParserHttp | ParserSecrets>();
      return DocumentSource.of({
        bytes: (document) => service.bytes(document).pipe(Effect.provide(context)),
      });
    }),
  );
}
